// - - - - - - - - - -
// #173 — Hard-surface filler-job recommender. The hard-surface analog of
// `recommendFillerCarpetJobs`, with the SAME four tiers (Expedite, RTS,
// Already-Scheduled, Repairs) and the SAME location / store / rtsDate / stops
// gates — those are reused directly (exported from recommendFillerCarpetJobs)
// so the two recommenders can't drift.
//
// The ONLY differences vs carpet:
//   • A job is classified into a TYPE by its main-SKU line items (ignore
//     doc.surface), and its size is the matched line items' `qty` sum (sqft).
//   • A candidate job fits if its sqft ≤ the installer's REMAINING for that
//     TYPE (and per-type furniture/step sub-caps), using the per-type numbers
//     the capacity engine already computed (`perType`).
//   • Tier 3 (Open Repairs) is surface-agnostic and identical to carpet —
//     repairs assigned to this installer, status-exclusion, repair-stops cap.

import { sumLineItemQtyForSkuList } from "./installerCapacityRemaining";
import { HARD_SURFACE_CAPACITY_TYPES } from "./keys";
import {
  installerCanWorkLocation,
  installerHasApprovedStore,
  isStrictlyAfterDate,
  isRtsReadyForCellDate,
  toPopoverEntry,
  closestFitCompare,
} from "./recommendFillerCarpetJobs";
import {
  parseInstallerLaborAttributes,
  installerHasRequiredLaborAttributes,
} from "./laborAttributesGate";

/**
 * Builds the four-tier recommendation list for a red HARD-SURFACE partial-
 * capacity cell.
 *
 * @param {Object} params
 * @param {Object} params.cellInstallerCandidate — installer whose cell is red
 * @param {string} params.cellDate               — "M/D/YYYY"
 * @param {Array}  params.perType                 — the capacity engine's per-type
 *                                                output: [{ type, capacityField,
 *                                                remaining, remainingFurniture,
 *                                                remainingSteps, canTakeJob }]
 * @param {number} [params.remainingJobStops]      — #146 jobs-stops hard cap
 * @param {number} [params.remainingRepairStops]   — #146 repairs-stops hard cap
 * @param {Array}  params.activeQueueDocs          — raw ActiveQueue docs
 * @param {Function} [params.normalizePayroll]     — payroll formatter
 * @returns {{expediteJobs, rtsJobs, alreadyScheduledJobs, repairJobs, totalCount}}
 *   Each entry carries `carpetYds` (= the job's sqft for its type — the popover
 *   labels it by `unit`), `surfaceType`, plus the standard popover fields.
 */
export function recommendFillerHardSurfaceJobs({
  cellInstallerCandidate,
  cellDate,
  perType,
  remainingJobStops,
  remainingRepairStops,
  activeQueueDocs,
  normalizePayroll,
  laborAttributeBySku,
  isOverCapacity,
}) {
  const empty = {
    expediteJobs: [],
    rtsJobs: [],
    alreadyScheduledJobs: [],
    repairJobs: [],
    totalCount: 0,
  };
  if (!cellInstallerCandidate || !cellDate) return empty;
  if (!Array.isArray(activeQueueDocs)) return empty;

  const parseConstraint = (v) => {
    if (v === undefined || v === null) return Infinity;
    const n = parseFloat(v);
    return isNaN(n) ? Infinity : n;
  };
  const noMoreJobsAllowed = parseConstraint(remainingJobStops) < 1;
  const noMoreRepairsAllowed = parseConstraint(remainingRepairStops) < 1;

  const normalize = normalizePayroll || ((v) => v);
  const companyCode = cellInstallerCandidate.companyCode;
  const homeLocation = cellInstallerCandidate.cLocationCode;
  const installerPayroll = normalize(cellInstallerCandidate.payrollNo);

  // #194 — installer's checked Labor-Catalog attributes (parsed once) for the
  // per-job attribute gate. Inert when laborAttributeBySku is empty/absent.
  const installerAttributeSet = parseInstallerLaborAttributes(cellInstallerCandidate);

  // Per-type remaining lookup (only the installer's configured types). Each
  // entry pairs the engine's remaining numbers with that type's SKU lists so
  // we can size + fit a candidate job against the right type.
  const typeConfigByField = new Map(
    HARD_SURFACE_CAPACITY_TYPES.map((t) => [t.capacityField, t])
  );
  // #177 (2026-06-03) — gate fillable types on `remaining > 0` ONLY, not on
  // `pt.canTakeJob`. `canTakeJob` carries the engine's `anyWorkToday` term
  // (≥1 job already on the day), which is correct for the cell's RED-border
  // flag but WRONG for the recommender: it suppressed all recommendations on
  // an EMPTY next-business-day cell (#155), where there's no work yet but the
  // installer has full first-job capacity. The carpet recommender already
  // recommends on empty cells (it gates on remaining yards, not work-today);
  // this restores parity for hard surface. The per-day job-stops cap is still
  // enforced below via `noMoreJobsAllowed` / `jobTierOpen`, so a work-today
  // cell with its stops maxed still recommends nothing.
  const fitTypes = (perType || [])
    .filter((pt) => pt.remaining > 0)
    .map((pt) => ({
      type: pt.type,
      remaining: pt.remaining,
      remainingFurniture: parseConstraint(pt.remainingFurniture),
      remainingSteps: parseConstraint(pt.remainingSteps),
      skus: typeConfigByField.get(pt.capacityField),
    }))
    .filter((pt) => pt.skus);

  // #192 follow-up (rule A): over capacity on any surface → no flooring job
  // tiers (Tier 1/2/4); only Tier 3 repairs (processed before this gate) remain.
  const jobTierOpen =
    fitTypes.length > 0 && !noMoreJobsAllowed && !isOverCapacity;

  // Nothing fillable at all → early out.
  if (!jobTierOpen && noMoreRepairsAllowed) return empty;

  const expediteJobs = [];
  const rtsJobs = [];
  const alreadyScheduledJobs = [];
  const repairJobs = [];

  // For a candidate (non-repair) doc, return the first type it fits, with the
  // sqft + furniture/step piece counts for that type — or null if it fits no
  // configured type with room.
  const firstFittingType = (doc) => {
    for (const ft of fitTypes) {
      const sqft = sumLineItemQtyForSkuList(doc, ft.skus.mainSkus);
      if (sqft <= 0) continue; // not this type
      if (sqft > ft.remaining) continue; // doesn't fit the sqft cap
      const furn = sumLineItemQtyForSkuList(doc, ft.skus.furnitureSkus);
      if (furn > ft.remainingFurniture) continue;
      const steps = sumLineItemQtyForSkuList(doc, ft.skus.stepSkus);
      if (steps > ft.remainingSteps) continue;
      return { type: ft.type, sqft, furn, steps };
    }
    return null;
  };

  for (const doc of activeQueueDocs) {
    if (!doc) continue;
    if (doc.companyCode !== companyCode) continue;
    const statusCode = parseFloat(doc.statusCode);

    // Tier 3 — OPEN REPAIRS (surface-agnostic; identical to carpet).
    if (doc.classification === "Repair Order") {
      if (normalize(doc.installer) !== installerPayroll) continue;
      const isExcluded =
        statusCode === 20 ||
        (statusCode > 4 && statusCode !== 30 && statusCode !== 30.1);
      if (isExcluded) continue;
      if (noMoreRepairsAllowed) continue;
      // Synthetic size = 1 (display only), like carpet's Tier 3. Furniture/step
      // piece counts aren't shown for repairs (surface-agnostic, no per-type
      // list applies), so they're 0.
      const entry = toPopoverEntry(doc, 1, 0, 0);
      entry.surfaceType = "Repair";
      entry.unit = "sqft";
      repairJobs.push(entry);
      continue;
    }

    // Tier 1 / 2 / 4 — classify + fit by type.
    if (!jobTierOpen) continue;
    // #194 — Labor-Catalog attribute gate (job tiers only; repairs exempt,
    // mirroring carpet + the #149 store-gate exemption). Inert pre-data.
    if (
      !installerHasRequiredLaborAttributes(
        doc,
        laborAttributeBySku,
        installerAttributeSet
      )
    )
      continue;
    const fit = firstFittingType(doc);
    if (!fit) continue;
    const makeEntry = () => {
      const e = toPopoverEntry(doc, fit.sqft, fit.furn, fit.steps);
      e.surfaceType = fit.type;
      e.unit = "sqft";
      return e;
    };

    const isTier1Or4Status = statusCode === 1.2 || statusCode === 4;

    if (doc.expediteFlag === true && isTier1Or4Status) {
      // Tier 1 — Expedite pull-forward at the cell's home location.
      if (doc.location !== homeLocation) continue;
      if (!isStrictlyAfterDate(doc.date_scheduled, cellDate)) continue;
      if (!isRtsReadyForCellDate(doc, cellDate)) continue;
      if (!installerHasApprovedStore(cellInstallerCandidate, doc.lowes_storeNo)) continue;
      expediteJobs.push(makeEntry());
    } else if (statusCode === 3 || statusCode === 3.1) {
      // Tier 2 — RTS unassigned at an eligible location for the installer.
      // #177/#179 (Brandon, 2026-06-03): include 3.1 (RTS-Suppressed) alongside
      // 3 (RTS). Brandon wants suppressed RTS jobs to still surface as fillers
      // (they're unassigned work that can be scheduled). Supersedes the #156
      // "no suppressed jobs in recommendations" spec for the RTS tier ONLY —
      // Expedite (Tier 1) + Already-Scheduled (Tier 4) still exclude 3.x.
      if (!installerCanWorkLocation(cellInstallerCandidate, doc.location)) continue;
      if (!installerHasApprovedStore(cellInstallerCandidate, doc.lowes_storeNo)) continue;
      rtsJobs.push(makeEntry());
    } else if (isTier1Or4Status) {
      // Tier 4 — non-expedite already-scheduled pull-forward (home location).
      if (doc.location !== homeLocation) continue;
      if (!isStrictlyAfterDate(doc.date_scheduled, cellDate)) continue;
      if (!isRtsReadyForCellDate(doc, cellDate)) continue;
      if (!installerHasApprovedStore(cellInstallerCandidate, doc.lowes_storeNo)) continue;
      alreadyScheduledJobs.push(makeEntry());
    }
  }

  expediteJobs.sort(closestFitCompare);
  rtsJobs.sort(closestFitCompare);
  alreadyScheduledJobs.sort(closestFitCompare);
  repairJobs.sort(closestFitCompare);

  return {
    expediteJobs,
    rtsJobs,
    alreadyScheduledJobs,
    repairJobs,
    totalCount:
      expediteJobs.length +
      rtsJobs.length +
      alreadyScheduledJobs.length +
      repairJobs.length,
  };
}
